import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { ClientProductCalculation } from 'types/clientProductTypes';
import {
  ClientProductFieldsetLayoutComponent,
  ClientProductFieldsetProgressComponent,
  ClientProductFieldsetRowComponent,
  ClientProductFieldsetValueComponent,
} from 'design/clientProductFieldset/clientProductFieldsetComponents';
import { TextInputControlComponent } from 'design/textInputControl';
import {
  INPUT_DISCOUNT_PERCENT_MAX_LENGTH,
  INPUT_PARTNER_PERCENT_MAX_LENGTH,
} from 'utils/constants';
import { ClientProductFormValues } from 'types/clientProductFormTypes';
import { M8ProductType } from 'services/production/types';
import { TextComponent } from 'design/text/textComponents';

type Props = {
  values: ClientProductFormValues;
  withPartnerPercent: boolean;
  calculation: ClientProductCalculation;
  m8ProductType: M8ProductType;
  onDiscountPercentChange(nextValue: string): void;
  onPartnerPercentChange(nextValue: string): void;
};

export const ClientProductFieldsetComponent = (props: Props) => {
  const {
    values: clientProductFormValues,
    withPartnerPercent,
    calculation: clientProductCalculation,
    onDiscountPercentChange,
    onPartnerPercentChange,
  } = props;

  const intl = useIntlDictionary();

  const {
    clientDetails: { productEditor: ownDictionary },
  } = intl;

  return (
    <ClientProductFieldsetLayoutComponent
      progress={
        <ClientProductFieldsetProgressComponent
          agmBarWidth={clientProductCalculation.agmPercent}
          agmPercentText={
            clientProductCalculation.agmPercent > 0 && (
              <TextComponent color="inherit" weight="bold">
                {ownDictionary.progressValue(
                  clientProductCalculation.agmPercent
                )}
              </TextComponent>
            )
          }
          partnerBarWidth={clientProductCalculation.partnerPercent}
          partnerPercentText={
            withPartnerPercent &&
            clientProductCalculation.partnerPercent > 0 && (
              <TextComponent color="inherit" weight="bold">
                {ownDictionary.progressValue(
                  clientProductCalculation.partnerPercent
                )}
              </TextComponent>
            )
          }
          clientBarWidth={clientProductCalculation.discountPercent}
          clientPercentText={
            clientProductCalculation.discountPercent > 0 && (
              <TextComponent color="inherit" weight="bold">
                {ownDictionary.progressValue(
                  clientProductCalculation.discountPercent
                )}
              </TextComponent>
            )
          }
        />
      }
      agmRow={
        <ClientProductFieldsetRowComponent
          theme="agm"
          label={<TextComponent>{ownDictionary.agmRowLabel}</TextComponent>}
          value={
            <ClientProductFieldsetValueComponent
              percent={<TextComponent>%</TextComponent>}
            >
              <TextComponent>
                {ownDictionary.agmRowValue(clientProductCalculation.agmPercent)}
              </TextComponent>
            </ClientProductFieldsetValueComponent>
          }
          result={
            <TextComponent color="secondary">
              {ownDictionary.agmRowResult(clientProductCalculation.agmAmount)}
            </TextComponent>
          }
        />
      }
      partnerRow={
        withPartnerPercent && (
          <ClientProductFieldsetRowComponent
            theme="partner"
            label={
              <TextComponent>{ownDictionary.partnerRowLabel}</TextComponent>
            }
            value={
              <ClientProductFieldsetValueComponent
                percent={<TextComponent>%</TextComponent>}
              >
                <TextComponent>
                  <TextInputControlComponent
                    maxLength={INPUT_PARTNER_PERCENT_MAX_LENGTH}
                    value={clientProductFormValues.partnerPercent}
                    onValueChange={onPartnerPercentChange}
                    autoFocus
                  />
                </TextComponent>
              </ClientProductFieldsetValueComponent>
            }
            result={
              <TextComponent color="secondary">
                {ownDictionary.clientRowResult(
                  clientProductCalculation.partnerAmount
                )}
              </TextComponent>
            }
          />
        )
      }
      clientRow={
        <ClientProductFieldsetRowComponent
          theme="client"
          label={<TextComponent>{ownDictionary.clientRowLabel}</TextComponent>}
          value={
            <ClientProductFieldsetValueComponent
              percent={<TextComponent>%</TextComponent>}
            >
              <TextComponent>
                <TextInputControlComponent
                  maxLength={INPUT_DISCOUNT_PERCENT_MAX_LENGTH}
                  value={clientProductFormValues.discountPercent}
                  onValueChange={onDiscountPercentChange}
                  autoFocus
                />
              </TextComponent>
            </ClientProductFieldsetValueComponent>
          }
          result={
            <TextComponent color="secondary">
              {ownDictionary.clientRowResult(
                clientProductCalculation.discountAmount
              )}
            </TextComponent>
          }
        />
      }
      summary={
        <ClientProductFieldsetRowComponent
          label={
            <TextComponent weight="medium">
              {ownDictionary.tariffRowLabel}
            </TextComponent>
          }
          value={null}
          result={
            <TextComponent>
              <TextComponent weight="medium">
                {ownDictionary.tariffRowValue(
                  clientProductCalculation.tariffAmount
                )}
              </TextComponent>{' '}
              {clientProductCalculation.discountPercent > 0 && (
                <TextComponent color="secondary">
                  {ownDictionary.tariffRowResult(
                    clientProductCalculation.discountPercent
                  )}
                </TextComponent>
              )}
            </TextComponent>
          }
        />
      }
    />
  );
};
